import {
  DropdownLayoutOption,
  DropdownLayoutValueOption,
  MultiSelectCheckbox,
  MultiSelectCheckboxProps,
} from '@wix/design-system';
import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { KeyedItem } from '@wix/bex-core';
import { DropdownSelectState } from '../../state';
import { FilterProps } from '../../model';
import { useWindowFlipPopoverProps } from '../../utils/useWindowFlipPopoverProps';

export type KeyedItemOption<T> = DropdownLayoutValueOption & {
  keyedItem: KeyedItem<T>;
};

export type MultiSelectCheckboxFilterBaseOption<V> =
  | KeyedItemOption<V>
  | DropdownLayoutOption;

export interface MultiSelectCheckboxFilterBaseProps<V>
  extends Partial<MultiSelectCheckboxProps>,
    FilterProps<V[]> {
  /**
   * Applies a data-hook HTML attribute that can be used in the tests
   * @default `collection-filter-${filter.name}`
   */
  dataHook?: string;
  options: MultiSelectCheckboxFilterBaseOption<V>[];
  dropdownSelect: DropdownSelectState<V>;
  placeholder?: string;
}

function _MultiSelectCheckboxFilterBase<V>(
  props: MultiSelectCheckboxFilterBaseProps<V>,
) {
  const {
    filter,
    dropdownSelect,
    options,
    placeholder,
    popoverProps,
    ...rest
  } = props;

  const {
    select: { selectedKeys, selectedValues },
    container: { translate: t },
  } = dropdownSelect;

  const windowFlipPopoverProps = useWindowFlipPopoverProps(popoverProps);

  const selectedLabel = useMemo(() => {
    if (selectedValues.length > 2) {
      return t('cairo.selected', {
        total: selectedValues.length,
      });
    }

    if (selectedValues.length) {
      return selectedValues.map(({ item }) => filter.itemName(item)).join(', ');
    }

    return '';
  }, [selectedValues]);

  return (
    <MultiSelectCheckbox
      dataHook={`collection-filter-${filter.name}`}
      border="round"
      textOverflow="ellipsis"
      options={options}
      value={selectedLabel}
      placeholder={placeholder ?? t('cairo.selectPlaceholder')}
      selectedOptions={selectedKeys}
      popoverProps={windowFlipPopoverProps}
      {...rest}
    />
  );
}

export const MultiSelectCheckboxFilterBase = observer(
  _MultiSelectCheckboxFilterBase,
);
